/**
 * While a menu, dropdown or popover is open on Home, a click (or tap) outside it only closes it: the
 * click never reaches what was under the pointer, even a live button, link or table row.
 *
 * How: the press is noticed in the capture phase, before anything else sees it. If a menu is open and
 * the press is outside every open menu, the press's focus change is cancelled and the click that follows
 * is stopped before it reaches its target. A stand-in click is then sent to the page body (or, when the
 * press was inside another open menu — e.g. a dropdown opened from within the date popover — to that
 * menu), so PrimeNG's own outside-click handling closes the menu exactly as before.
 */
const OPEN_MENU_SELECTOR = [
  '.p-menu-overlay',
  '.p-tieredmenu-overlay',
  '.p-contextmenu',
  '.p-popover',
  '.p-confirmpopup',
  '.p-select-overlay',
  '.p-multiselect-overlay',
  '.p-autocomplete-overlay',
  '.p-datepicker-panel:not(.p-datepicker-panel-inline)',
  '.home-zoom-panel',
].join(',');

export class OutsideClickGuard {
  /** Set by a press outside an open menu; the click it produces is swallowed. */
  private swallowNextClick = false;
  /** Where the stand-in click goes: the open menu the press landed in, or the body. */
  private redirectTarget: HTMLElement | null = null;
  private attached = false;

  constructor(private readonly doc: Document = document) {}

  attach(): void {
    if (this.attached) {
      return;
    }
    this.attached = true;
    const win = this.doc.defaultView;
    win?.addEventListener('pointerdown', this.onPointerDown, true);
    win?.addEventListener('mousedown', this.onMouseDown, true);
    win?.addEventListener('pointercancel', this.onPointerCancel, true);
    win?.addEventListener('click', this.onClick, true);
  }

  detach(): void {
    if (!this.attached) {
      return;
    }
    this.attached = false;
    const win = this.doc.defaultView;
    win?.removeEventListener('pointerdown', this.onPointerDown, true);
    win?.removeEventListener('mousedown', this.onMouseDown, true);
    win?.removeEventListener('pointercancel', this.onPointerCancel, true);
    win?.removeEventListener('click', this.onClick, true);
    this.reset();
  }

  private openMenus(): HTMLElement[] {
    return Array.from(this.doc.querySelectorAll<HTMLElement>(OPEN_MENU_SELECTOR)).filter(
      (el) => el.getClientRects().length > 0
    );
  }

  /** The menu on top: the highest z-index (PrimeNG raises each overlay it opens — set on the overlay
   * or on its wrapper), else the last in the page. */
  private topmost(menus: HTMLElement[]): HTMLElement {
    const zIndex = (el: HTMLElement) => {
      for (let node: HTMLElement | null = el; node && node !== this.doc.body; node = node.parentElement) {
        const z = Number.parseInt(this.doc.defaultView?.getComputedStyle(node).zIndex ?? '', 10);
        if (Number.isFinite(z)) {
          return z;
        }
      }
      return 0;
    };
    return menus.reduce((top, menu) => (zIndex(menu) >= zIndex(top) ? menu : top));
  }

  private readonly onPointerDown = (event: PointerEvent): void => {
    this.reset();
    if (event.button !== 0 && event.pointerType === 'mouse') {
      return; // right/middle clicks are left alone
    }
    const menus = this.openMenus();
    if (!menus.length) {
      return;
    }
    const target = event.target as Node | null;
    const containing = target ? menus.filter((menu) => menu.contains(target)) : [];
    if (containing.length === menus.length || (target && this.topmost(menus).contains(target))) {
      // Inside the open menu(s), or inside the one opened last — e.g. an option of a dropdown opened
      // from within the date popover: an ordinary click.
      return;
    }
    // Outside a menu. If the press is in another open menu (e.g. the date popover while a dropdown
    // opened from it is showing), the stand-in click goes to that menu so it stays open.
    this.redirectTarget = containing.find((menu) => !containing.some((other) => other !== menu && menu.contains(other))) ?? this.doc.body;
    this.swallowNextClick = true;
  };

  /** Keeps the press from focusing a field or button under it (the press only closes the menu). */
  private readonly onMouseDown = (event: MouseEvent): void => {
    if (this.swallowNextClick) {
      event.preventDefault();
    }
  };

  /** A touch that turned into a scroll produces no click. */
  private readonly onPointerCancel = (): void => this.reset();

  private readonly onClick = (event: MouseEvent): void => {
    if (!this.swallowNextClick) {
      return;
    }
    const redirect = this.redirectTarget ?? this.doc.body;
    this.reset();
    event.preventDefault();
    event.stopImmediatePropagation();
    redirect.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: this.doc.defaultView }));
  };

  private reset(): void {
    this.swallowNextClick = false;
    this.redirectTarget = null;
  }
}
